import React from 'react';
import { Card } from 'antd';
import { Chart as ChartJS } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { Chart } from 'react-chartjs-2';

/*
 * Throwaway test view to validate chartjs-chart-matrix@2 (heatmap) against the
 * project's chart.js@4 / react-chartjs-2@5 stack.
 *
 * Controller + element are registered GLOBALLY via ChartJS.register (same
 * pattern used for datalabels/zoom/annotation). The matrix chart type is then
 * rendered through react-chartjs-2's generic <Chart type="matrix" />.
 *
 * Reproduces the p.2 "Domain Deep Dive" heatmap: entities (y) x dates (x),
 * each cell colored by its health-score bucket.
 *
 * Route: /dev/heatmap-test  (remove once validated)
 */

ChartJS.register(MatrixController, MatrixElement);

const ENTITIES = [
  'geotab_fault_data', 'geotab_zone', 'geotab_log_record', 'geotab_trip',
  'geotab_status_data', 'geotab_rule', 'geotab_device_status_info',
  'geotab_route_plan_item', 'geotab_group', 'geotab_route',
  'geotab_planned_vs', 'geotab_zone_type', 'geotab_device',
];

const DATES = [
  '04-18', '04-21', '04-24', '04-27', '04-30',
  '05-03', '05-06', '05-09', '05-12', '05-15',
];

// Deterministic pseudo health scores (no randomness) so the view is stable.
// Mixes buckets per row so every color band is exercised.
function healthFor(rowIdx, colIdx) {
  const base = 60 + ((rowIdx * 7 + colIdx * 13) % 41); // 60..100
  if ((rowIdx + colIdx) % 17 === 0) return null;        // a few "sin datos" cells
  return base;
}

// Bucket -> color, matching the dashboard legend.
function bucketColor(v) {
  if (v === null || v === undefined) return '#e5e7eb'; // sin datos (gray)
  if (v >= 90) return '#15803d';                        // >=90  dark green
  if (v >= 75) return '#86efac';                        // 75-90 light green
  if (v >= 60) return '#d97706';                        // 60-75 orange
  return '#dc2626';                                     // <60   red
}

const matrixData = [];
ENTITIES.forEach((entity, r) => {
  DATES.forEach((date, c) => {
    matrixData.push({ x: date, y: entity, v: healthFor(r, c) });
  });
});

const data = {
  datasets: [
    {
      label: 'health score',
      data: matrixData,
      backgroundColor: (ctx) => bucketColor(ctx.raw.v),
      borderColor: 'rgba(255,255,255,0.6)',
      borderWidth: 1,
      width: ({ chart }) =>
        ((chart.chartArea || {}).width || 0) / DATES.length - 2,
      height: ({ chart }) =>
        ((chart.chartArea || {}).height || 0) / ENTITIES.length - 2,
    },
  ],
};

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: () => '',
        label: (ctx) => {
          const { x, y, v } = ctx.raw;
          return `${y} @ ${x}: ${v === null ? 'sin datos' : v}`;
        },
      },
    },
  },
  scales: {
    x: {
      type: 'category',
      labels: DATES,
      offset: true,
      grid: { display: false },
      ticks: { autoSkip: false },
    },
    y: {
      type: 'category',
      labels: [...ENTITIES].reverse(),
      offset: true,
      grid: { display: false },
    },
  },
};

function HeatmapTest() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-2">chartjs-chart-matrix — heatmap test view</h1>
      <p className="text-gray-500 mb-4">
        Validates the matrix (heatmap) chart type on the existing chart.js@4
        stack. Reproduces the p.2 domain heatmap. Safe to delete.
      </p>

      <Card title="GEOTAB — health score heatmap (entities x dates)">
        <div style={{ height: 460 }}>
          <Chart type="matrix" data={data} options={options} />
        </div>
        <div className="flex gap-4 mt-4 text-xs text-gray-600">
          <span><span style={{ background: '#15803d' }} className="inline-block w-3 h-3 mr-1 align-middle" />&gt;=90</span>
          <span><span style={{ background: '#86efac' }} className="inline-block w-3 h-3 mr-1 align-middle" />75-90</span>
          <span><span style={{ background: '#d97706' }} className="inline-block w-3 h-3 mr-1 align-middle" />60-75</span>
          <span><span style={{ background: '#dc2626' }} className="inline-block w-3 h-3 mr-1 align-middle" />&lt;60</span>
          <span><span style={{ background: '#e5e7eb' }} className="inline-block w-3 h-3 mr-1 align-middle" />sin datos</span>
        </div>
      </Card>
    </div>
  );
}

export default HeatmapTest;
